import { ComponentType, ReactNode, useMemo, useState } from "react";

import { LinearProgress, SxProps, Theme } from "@mui/material";
import {
  DataGridProps,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridInputRowSelectionModel,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
} from "@mui/x-data-grid";

import { TableCellLoadingSkeletonWrapper } from "../../SolarGikLib/loaders/TableCellLoadingSkeletonWrapper";
import { TABLE_NO_DATA_MESSAGE } from "../ConstantValues";
import { StyledDataGridTable, GridToolbarStyled } from "../Mui/StyledDataGridTable";
import { createSkeletonRows } from "./SkeletonUtils";

export interface ExternalRowsSelectionProps {
  selectedRows: GridInputRowSelectionModel;
  setSelectedRows: (selectedRows: GridRowSelectionModel) => void;
}

export type TableColumnDef<Row extends GridValidRowModel = GridValidRowModel> = GridColDef<Row> & {
  includeInExport?: boolean;
};

interface ITableProps<Row extends GridValidRowModel> extends Omit<
  DataGridProps<Row>,
  "onRowSelectionModelChange"
> {
  tableKey: string;
  loading?: boolean;
  numberOfSkeletonRows?: number;
  rows: Row[];
  columns: TableColumnDef<Row>[];
  hideToolbar?: boolean;
  hiddenColumns?: string[];
  hideHeaders?: boolean;
  hideBorder?: boolean;
  isHideGridColumns?: boolean;
  noRowsMessage?: string;
  rowSelection?: boolean;
  externalRowsSelectionProps?: ExternalRowsSelectionProps;
  customUI?: SxProps<Theme>;
  customTableComponent?: typeof StyledDataGridTable;
  columnVisibilityStorageKey?: string;
  toolbarContent?: ReactNode;
  toolbarActions?: ReactNode;
  getCsvRows?: (fields: string[]) => string[][];
  fitToPage?: boolean;
}

const Table = <Row extends GridValidRowModel>({
  tableKey,
  loading = false,
  numberOfSkeletonRows = 6,
  experimentalFeatures,
  rows,
  columns,
  paginationMode,
  paginationModel,
  onCellClick,
  initialState,
  hiddenColumns,
  hideFooter = false,
  hideToolbar = false,
  hideBorder = false,
  hideHeaders = false,
  isHideGridColumns = false,
  hideFooterPagination = true,
  pageSizeOptions = [10],
  noRowsMessage = TABLE_NO_DATA_MESSAGE,
  rowSelection = true,
  customUI,
  externalRowsSelectionProps,
  getRowClassName = (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"),
  customTableComponent: StyledTable = StyledDataGridTable,
  getRowId,
  columnVisibilityStorageKey,
  toolbarContent,
  toolbarActions,
  getCsvRows,
  fitToPage = false,
  slots,
  slotProps,
  ...otherProps
}: ITableProps<Row>) => {
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortModel, setSortModel] = useState<GridSortModel>();

  const initialColumnVisibility = useMemo<GridColumnVisibilityModel | undefined>(() => {
    if (!columnVisibilityStorageKey) {
      return undefined;
    }
    try {
      const stored = localStorage.getItem(columnVisibilityStorageKey);
      const parsed: unknown = stored ? JSON.parse(stored) : undefined;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const defaults = initialState?.columns?.columnVisibilityModel;
        return Object.fromEntries(
          columns.map((column) => {
            const value = (parsed as Record<string, unknown>)[column.field];
            return [
              column.field,
              column.hideable === false
                ? true
                : typeof value === "boolean"
                  ? value
                  : (defaults?.[column.field] ?? true),
            ];
          })
        );
      }
    } catch {
      // Persistence is optional when storage is malformed or unavailable.
    }
    return initialState?.columns?.columnVisibilityModel;
  }, [columnVisibilityStorageKey, initialState, columns]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    GridColumnVisibilityModel | undefined
  >(initialColumnVisibility);

  const handleColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    if (columnVisibilityStorageKey) {
      try {
        localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(model));
      } catch {
        // Keep the table usable when browser storage is unavailable.
      }
    }
    setColumnVisibilityModel(model);
  };

  const isExternallyControlled = externalRowsSelectionProps != undefined;
  const filteredColumns = hiddenColumns
    ? columns.filter((column) => !hiddenColumns.includes(column.field))
    : columns;
  const rowsToShow = loading
    ? (createSkeletonRows(numberOfSkeletonRows, columns) as unknown as Row[])
    : rows;
  const dataGridKey = `${tableKey}-${loading ? "loading" : "data"}`;
  const TypedStyledTable = StyledTable as ComponentType<DataGridProps<Row>>;
  const columnsWithLoading = filteredColumns.map((column) => {
    const { includeInExport, ...gridColumn } = column;
    return {
      ...gridColumn,
      disableExport: includeInExport === undefined ? gridColumn.disableExport : !includeInExport,
      renderHeader: loading
        ? () => (
            <TableCellLoadingSkeletonWrapper loading={loading}>
              <span>{column.headerName}</span>
            </TableCellLoadingSkeletonWrapper>
          )
        : gridColumn.renderHeader, //This very very important line for multisite table will not work without it!
      renderCell: (params) => (
        <TableCellLoadingSkeletonWrapper loading={loading}>
          {gridColumn.renderCell ? gridColumn.renderCell(params) : params.value}
        </TableCellLoadingSkeletonWrapper>
      ),
    } as GridColDef<Row>;
  });

  const resolvedToolbar = hideToolbar ? undefined : (slots?.toolbar ?? GridToolbarStyled);

  return (
    <TypedStyledTable
      key={dataGridKey}
      onCellClick={onCellClick}
      slots={{
        loadingOverlay: LinearProgress,
        ...slots,
        toolbar: resolvedToolbar,
      }}
      slotProps={{
        ...slotProps,
        toolbar: {
          ...(slotProps?.toolbar as object),
          toolbarContent,
          toolbarActions,
          getCsvRows,
        },
      }}
      experimentalFeatures={experimentalFeatures}
      rows={rowsToShow}
      columns={columnsWithLoading}
      paginationMode={paginationMode ?? (fitToPage ? "client" : undefined)}
      paginationModel={paginationModel}
      autoPageSize={fitToPage}
      hideFooterPagination={fitToPage ? false : hideFooterPagination}
      pageSizeOptions={fitToPage ? [] : pageSizeOptions}
      hideFooter={hideFooter}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      filterModel={filterModel}
      onFilterModelChange={(model) => setFilterModel(model)}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      getRowClassName={getRowClassName}
      getRowId={loading ? undefined : getRowId}
      {...(isExternallyControlled && {
        onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
          if (!rowSelection) {
            return;
          }
          externalRowsSelectionProps.setSelectedRows(newRowSelectionModel);
        },
        rowSelectionModel: externalRowsSelectionProps.selectedRows,
      })}
      keepNonExistentRowsSelected
      initialState={initialState}
      localeText={{
        toolbarColumns: "",
        toolbarFilters: "",
        toolbarExport: "",
        noRowsLabel: noRowsMessage,
      }}
      sx={{
        minWidth: 0,
        ...(fitToPage && { height: "100%", minHeight: 0 }),
        ...(onCellClick
          ? {
              "& .MuiDataGrid-cell": {
                cursor: "pointer",
              },
            }
          : {}),
        ...(hideHeaders && {
          "& .MuiDataGrid-columnHeaders": { display: "none" },
        }),
        ...(hideBorder && { "& .MuiDataGrid-main": { border: "none" } }),
        ...(isHideGridColumns && {
          "& .MuiDataGrid-cell": { borderRight: "0px", border: "0px" },
        }),
        ...(customUI && { ...customUI }),
      }}
      rowSelection={rowSelection}
      {...otherProps}
    />
  );
};

export default Table;
